import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Trophy, 
  Lock, 
  CheckCircle, 
  Star, 
  Target,
  Filter,
  Search
} from 'lucide-react';
import { useGameStore } from '../store/gameStore';
import { Achievement } from '../types/game';

const Achievements: React.FC = () => {
  const { unlockedAchievements, stats, completedDecisions, currentAge } = useGameStore();
  const [filter, setFilter] = useState<'all' | 'unlocked' | 'locked'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // This would typically come from the store, but for now we'll use the data
  const allAchievements: Achievement[] = [
    {
      id: 'achievement-1',
      title: 'First Steps',
      description: 'Complete your first life decision',
      icon: '👶',
      color: '#fbbf24',
      unlocked: unlockedAchievements.includes('achievement-1'),
      requirements: {
        decisions: ['decision-1'],
      },
    },
    {
      id: 'achievement-2',
      title: 'Scholar',
      description: 'Achieve high education level',
      icon: '🎓',
      color: '#8b5cf6',
      unlocked: stats.education >= 80,
      requirements: {
        stats: { education: 80 },
      },
    },
    {
      id: 'achievement-3',
      title: 'Wealthy',
      description: 'Accumulate significant wealth',
      icon: '💰',
      color: '#10b981',
      unlocked: stats.wealth >= 85,
      requirements: {
        stats: { wealth: 85 },
      },
    },
    {
      id: 'achievement-4',
      title: 'Healthy Living',
      description: 'Maintain excellent health',
      icon: '💪',
      color: '#ef4444',
      unlocked: stats.health >= 90,
      requirements: {
        stats: { health: 90 },
      },
    },
    {
      id: 'achievement-5',
      title: 'Social Butterfly',
      description: 'Build strong relationships',
      icon: '🦋',
      color: '#ec4899',
      unlocked: stats.relationships >= 85,
      requirements: {
        stats: { relationships: 85 },
      },
    },
    {
      id: 'achievement-6',
      title: 'Career Master',
      description: 'Reach the top of your career',
      icon: '👑',
      color: '#3b82f6',
      unlocked: stats.career >= 90,
      requirements: {
        stats: { career: 90 },
      },
    },
    {
      id: 'achievement-7',
      title: 'Balanced Life',
      description: 'Maintain balance across all areas',
      icon: '⚖️',
      color: '#f59e0b',
      unlocked: Object.values(stats).every(v => v >= 70),
      requirements: {
        stats: { health: 70, happiness: 70, wealth: 70, relationships: 70, education: 70, career: 70 },
      },
    },
    {
      id: 'achievement-8',
      title: 'Life Learner',
      description: 'Complete 10 major decisions',
      icon: '📚',
      color: '#8b5cf6',
      unlocked: completedDecisions.length >= 10,
      requirements: {
        decisions: ['decision-1', 'decision-2', 'decision-3', 'decision-4', 'decision-5', 'decision-6', 'decision-7', 'decision-8', 'decision-9', 'decision-10'],
      },
    },
    {
      id: 'achievement-9',
      title: 'Golden Years',
      description: 'Reach senior age with high satisfaction',
      icon: '🌅',
      color: '#f59e0b',
      unlocked: currentAge >= 65 && stats.happiness >= 80,
      requirements: {
        age: 65,
        stats: { happiness: 80 },
      },
    },
    {
      id: 'achievement-10',
      title: 'Perfect Score',
      description: 'Achieve maximum score',
      icon: '🏆',
      color: '#ffd700',
      unlocked: Object.values(stats).every(v => v >= 95),
      requirements: {
        stats: { health: 95, happiness: 95, wealth: 95, relationships: 95, education: 95, career: 95 },
      },
    },
  ];

  const filteredAchievements = allAchievements.filter(achievement => {
    const matchesFilter = filter === 'all' || 
      (filter === 'unlocked' && achievement.unlocked) || 
      (filter === 'locked' && !achievement.unlocked);
    
    const matchesSearch = achievement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      achievement.description.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesFilter && matchesSearch;
  });

  const unlockedCount = allAchievements.filter(a => a.unlocked).length;
  const totalCount = allAchievements.length;

  const getProgressColor = (achievement: Achievement) => {
    if (achievement.unlocked) return 'text-success-600';
    
    // Calculate progress for locked achievements
    if (achievement.requirements.stats) {
      const requiredStats = Object.entries(achievement.requirements.stats);
      const currentProgress = requiredStats.reduce((acc, [stat, required]) => {
        const current = stats[stat as keyof typeof stats];
        return acc + Math.min(current / (required || 100), 1);
      }, 0) / requiredStats.length;
      
      if (currentProgress >= 0.8) return 'text-warning-600';
      if (currentProgress >= 0.5) return 'text-orange-600';
      return 'text-slate-400';
    }
    
    return 'text-slate-400';
  };

  const getProgressPercentage = (achievement: Achievement) => {
    if (achievement.unlocked) return 100;
    
    if (achievement.requirements.stats) {
      const requiredStats = Object.entries(achievement.requirements.stats);
      const currentProgress = requiredStats.reduce((acc, [stat, required]) => {
        const current = stats[stat as keyof typeof stats];
        return acc + Math.min(current / (required || 100), 1);
      }, 0) / requiredStats.length;
      
      return Math.round(currentProgress * 100);
    }
    
    return 0;
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <h1 className="text-4xl font-bold text-slate-800 mb-4">Achievements</h1>
        <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-6">
          Unlock achievements by reaching milestones and completing challenges
        </p>
        
        {/* Progress Overview */}
        <div className="bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl p-6 text-white">
          <div className="flex items-center justify-center space-x-8">
            <div className="text-center">
              <div className="text-3xl font-bold">{unlockedCount}</div>
              <div className="text-primary-100">Unlocked</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">{totalCount}</div>
              <div className="text-primary-100">Total</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold">{Math.round((unlockedCount / totalCount) * 100)}%</div>
              <div className="text-primary-100">Complete</div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Filters and Search */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="flex flex-col sm:flex-row gap-4 items-center justify-between"
      >
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            placeholder="Search achievements..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-3 bg-white/80 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {[
            { id: 'all', name: 'All', icon: Filter },
            { id: 'unlocked', name: 'Unlocked', icon: CheckCircle },
            { id: 'locked', name: 'Locked', icon: Lock },
          ].map((filterOption) => {
            const Icon = filterOption.icon;
            return (
              <button
                key={filterOption.id}
                onClick={() => setFilter(filterOption.id as any)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-300 ${
                  filter === filterOption.id
                    ? 'bg-primary-100 text-primary-700 shadow-soft'
                    : 'bg-white/80 text-slate-600 hover:bg-primary-50 hover:text-primary-600'
                }`}
              >
                <Icon size={18} />
                <span className="font-medium">{filterOption.name}</span>
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* Achievements Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {filteredAchievements.map((achievement, index) => (
          <motion.div
            key={achievement.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 }}
            whileHover={{ scale: 1.02, y: -4 }}
            className={`p-6 rounded-2xl border-2 transition-all duration-300 ${
              achievement.unlocked
                ? 'bg-gradient-to-br from-success-50 to-success-100 border-success-200 shadow-medium'
                : 'bg-white/80 border-slate-200 hover:border-slate-300'
            }`}
          >
            {/* Achievement Icon */}
            <div className="flex items-center justify-between mb-4">
              <div 
                className={`w-16 h-16 rounded-2xl flex items-center justify-center text-3xl shadow-medium ${
                  achievement.unlocked 
                    ? 'bg-gradient-to-br from-success-500 to-success-600' 
                    : 'bg-slate-100'
                }`}
              >
                {achievement.unlocked ? achievement.icon : '🔒'}
              </div>
              {achievement.unlocked && (
                <CheckCircle size={24} className="text-success-600" />
              )}
            </div>

            {/* Achievement Info */}
            <div className="space-y-3">
              <h3 className={`text-xl font-bold ${
                achievement.unlocked ? 'text-success-800' : 'text-slate-800'
              }`}>
                {achievement.title}
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {achievement.description}
              </p>

              {/* Progress Bar */}
              {!achievement.unlocked && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm text-slate-600">
                    <span>Progress</span>
                    <span className="font-medium">{getProgressPercentage(achievement)}%</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <motion.div
                      className={`h-2 rounded-full ${
                        getProgressPercentage(achievement) >= 80 ? 'bg-warning-500' :
                        getProgressPercentage(achievement) >= 50 ? 'bg-orange-500' :
                        'bg-slate-400'
                      }`}
                      initial={{ width: 0 }}
                      animate={{ width: `${getProgressPercentage(achievement)}%` }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                    />
                  </div>
                </div>
              )}

              {/* Requirements */}
              {!achievement.unlocked && achievement.requirements.stats && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <h4 className="text-sm font-semibold text-slate-700 mb-2">Requirements:</h4>
                  <div className="space-y-1">
                    {Object.entries(achievement.requirements.stats).map(([stat, required]) => {
                      const current = stats[stat as keyof typeof stats];
                      const progress = Math.min(current / (required || 100), 1);
                      
                      return (
                        <div key={stat} className="flex items-center justify-between text-sm">
                          <span className="capitalize text-slate-600">{stat}</span>
                          <span className={`font-medium ${
                            progress >= 1 ? 'text-success-600' : 'text-slate-500'
                          }`}>
                            {current}/{required}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </motion.div>

      {/* Empty State */}
      {filteredAchievements.length === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4 }}
          className="text-center py-16 bg-white/50 rounded-2xl border border-slate-200"
        >
          <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Trophy size={40} className="text-slate-400" />
          </div>
          <h3 className="text-2xl font-semibold text-slate-600 mb-4">No Achievements Found</h3>
          <p className="text-slate-500 max-w-md mx-auto">
            {searchTerm 
              ? `No achievements match "${searchTerm}". Try a different search term.`
              : `No ${filter} achievements found.`
            }
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default Achievements;
